/**
 * Calendar days in the account's time zone (users.timezone), not the browser's.
 *
 * "Today", due-date labels, the weekly chart and the streak all count days where the user said
 * they live, so they match on every device and line up with the server's daily AI quota.
 * Days are compared as 'YYYY-MM-DD' keys; no date library needed.
 */
import { browserTimeZone } from './timeZone';

let activeTimeZone: string | undefined;

const isValidTimeZone = (zone: string): boolean => {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
};

/** Set from the signed-in account (useLocalePreferencesSync); undefined falls back to the browser. */
export const setActiveTimeZone = (zone: string | null | undefined): void => {
  activeTimeZone = zone && isValidTimeZone(zone) ? zone : undefined;
};

export const getActiveTimeZone = (): string | undefined => activeTimeZone ?? browserTimeZone();

const keyFormatters = new Map<string, Intl.DateTimeFormat>();

const keyFormatter = (zone: string | undefined): Intl.DateTimeFormat => {
  const id = zone ?? '';
  let formatter = keyFormatters.get(id);
  if (!formatter) {
    // en-CA formats dates as YYYY-MM-DD
    formatter = new Intl.DateTimeFormat('en-CA', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' });
    keyFormatters.set(id, formatter);
  }
  return formatter;
};

/** The calendar day of an instant as 'YYYY-MM-DD' in the given (default: active) zone. */
export const dayKey = (instant: Date | number = new Date(), zone: string | undefined = getActiveTimeZone()): string =>
  keyFormatter(zone).format(typeof instant === 'number' ? new Date(instant) : instant);

export const todayKey = (): string => dayKey(new Date());

const dayNumber = (key: string): number => {
  const [y, m, d] = key.split('-').map(Number);
  return Math.round(Date.UTC(y, m - 1, d) / 86_400_000);
};

/** Whole days from `from` to `to` (both 'YYYY-MM-DD'). */
export const daysBetween = (from: string, to: string): number => dayNumber(to) - dayNumber(from);

/** 'YYYY-MM-DD' shifted by a number of days. */
export const addDaysToKey = (key: string, days: number): string => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
};

/** 'YYYY-MM-DD' shifted by calendar months (Jan 31 + 1 month -> Feb 28/29). */
export const addMonthsToKey = (key: string, months: number): string => {
  const [y, m, d] = key.split('-').map(Number);
  const lastDay = new Date(Date.UTC(y, m - 1 + months + 1, 0)).getUTCDate();
  return new Date(Date.UTC(y, m - 1 + months, Math.min(d, lastDay))).toISOString().slice(0, 10);
};

/** A day key as a Date at UTC midnight; format it with timeZone 'UTC' so the day never shifts. */
export const keyToUtcDate = (key: string): Date => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

/** Milliseconds the zone is ahead of UTC at `instant`. */
const zoneOffset = (instant: number, zone: string | undefined): number => {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(new Date(instant));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return asUtc - Math.floor(instant / 1000) * 1000;
};

/** Wall-clock 'YYYY-MM-DDTHH:mm' in the zone -> the instant it denotes. */
export const wallTimeToInstant = (value: string, zone: string | undefined = getActiveTimeZone()): Date | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value);
  if (!match) return null;
  const [, y, mo, d, h, mi] = match.map(Number);
  const wall = Date.UTC(y, mo - 1, d, h, mi);
  // Two passes settle the offset around daylight-saving switches
  let instant = wall - zoneOffset(wall, zone);
  instant = wall - zoneOffset(instant, zone);
  return new Date(instant);
};

/** An instant as wall-clock 'YYYY-MM-DDTHH:mm' in the zone (for <input type="datetime-local">). */
export const instantToWallTime = (instant: Date, zone: string | undefined = getActiveTimeZone()): string => {
  const shifted = new Date(instant.getTime() + zoneOffset(instant.getTime(), zone));
  return shifted.toISOString().slice(0, 16);
};
